import { MCPConfiguration, MCPConfigInput } from "../../model/mcpConfig/MCPConfiguration.model";
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { AuthClientProvider } from '../../auth/AuthClientProvider';
import { Logger } from '../../utils/Logger';
import { deleteRequest } from "../utils/DeleteRequest";

// API endpoints based on user specification - deployed at nebulaapiuat6
const MCP_CONFIG_BASE_URL_DEV = "https://nebulaapiuat6.azurewebsites.net";
const MCP_CONFIG_BASE_URL_UAT = "https://nebulaapiuat6.azurewebsites.net";
const MCP_CONFIG_BASE_URL_PROD = "https://nebulaapi6.azurewebsites.net";

export const getMCPConfigBaseUrl = (env: string): string => {
  switch (env.toLowerCase()) {
    case 'dev':
      return MCP_CONFIG_BASE_URL_DEV;
    case 'uat':
      return MCP_CONFIG_BASE_URL_UAT;
    case 'prod':
      // Use environment-based URL when prod is ready
      return MCP_CONFIG_BASE_URL_PROD;
    default:
      return MCP_CONFIG_BASE_URL_UAT;
  }
};

const getMCPConfigFullUrl = (env: string): string => {
  const baseUrl = getMCPConfigBaseUrl(env);
  if (!baseUrl) {
    return '';
  }
  return `${baseUrl}/${env}/mcpconfigs`;
};

/**
 * Create a new MCP Configuration
 * POST /{environment}/mcpconfigs
 */
export const createMCPConfig = async (
  payload: MCPConfigInput,
  selectedEnv: string
): Promise<MCPConfiguration> => {
  const correlationId = uuidv4();
  const url = getMCPConfigFullUrl(selectedEnv);
  
  if (!url) {
    throw new Error(`MCP Config API not available for environment: ${selectedEnv}`);
  }

  Logger.Instance.trackTrace(`start createMCPConfig`, {
    url: url,
    correlationId: correlationId,
    reqPayload: payload
  });

  try {
    const response = await axios.post<MCPConfiguration>(url, payload, {
      headers: {
        'Authorization': await AuthClientProvider.getInstance().getAuthToken(selectedEnv),
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "x-ms-correlation-id": correlationId,
        "CorrelationId": correlationId
      },
    });

    Logger.Instance.trackTrace(`completed createMCPConfig`, {
      url: url,
      correlationId: correlationId,
      response: response
    });

    return response.data;
  } catch (error) {
    Logger.Instance.trackException({ error: error as Error }, {
      event: `error createMCPConfig`,
      url: url,
      correlationId: correlationId
    });
    throw error;
  }
};

/**
 * Get all MCP Configurations
 * GET /{environment}/mcpconfigs
 */
export const getMCPConfigs = async (
  selectedEnv: string
): Promise<MCPConfiguration[]> => {
  const correlationId = uuidv4();
  const url = getMCPConfigFullUrl(selectedEnv);
  
  if (!url) {
    throw new Error(`MCP Config API not available for environment: ${selectedEnv}`);
  }

  Logger.Instance.trackTrace(`start getMCPConfigs`, {
    url: url,
    correlationId: correlationId
  });

  try {
    const response = await axios.get<MCPConfiguration[]>(url, {
      headers: {
        'Authorization': await AuthClientProvider.getInstance().getAuthToken(selectedEnv),
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "x-ms-correlation-id": correlationId,
      },
    });

    Logger.Instance.trackTrace(`completed getMCPConfigs`, {
      url: url,
      correlationId: correlationId
    });

    return response.data;
  } catch (error) {
    Logger.Instance.trackException({ error: error as Error }, {
      event: `error getMCPConfigs`,
      url: url,
      correlationId: correlationId
    });
    throw error;
  }
};

/**
 * Get MCP Configuration by ID
 * GET /{environment}/mcpconfigs/{id}
 */
export const getMCPConfigById = async (
  id: string,
  selectedEnv: string
): Promise<MCPConfiguration> => {
  const correlationId = uuidv4();
  const baseUrl = getMCPConfigFullUrl(selectedEnv);
  const url = `${baseUrl}/${id}`;
  
  if (!baseUrl) {
    throw new Error(`MCP Config API not available for environment: ${selectedEnv}`);
  }

  Logger.Instance.trackTrace(`start getMCPConfigById`, {
    url: url,
    correlationId: correlationId
  });

  try {
    const response = await axios.get<MCPConfiguration>(url, {
      headers: {
        'Authorization': await AuthClientProvider.getInstance().getAuthToken(selectedEnv),
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "x-ms-correlation-id": correlationId,
      },
    });

    Logger.Instance.trackTrace(`completed getMCPConfigById`, {
      url: url,
      correlationId: correlationId
    });

    return response.data;
  } catch (error) {
    Logger.Instance.trackException({ error: error as Error }, {
      event: `error getMCPConfigById`,
      url: url,
      correlationId: correlationId
    });
    throw error;
  }
};

/**
 * Update MCP Configuration
 * PATCH /{environment}/mcpconfigs/{id}
 */
export const updateMCPConfig = async (
  id: string,
  payload: MCPConfigInput,
  selectedEnv: string
): Promise<MCPConfiguration> => {
  const correlationId = uuidv4();
  const baseUrl = getMCPConfigFullUrl(selectedEnv);
  const url = `${baseUrl}/${id}`;
  
  if (!baseUrl) {
    throw new Error(`MCP Config API not available for environment: ${selectedEnv}`);
  }

  Logger.Instance.trackTrace(`start updateMCPConfig`, {
    url: url,
    correlationId: correlationId,
    reqPayload: payload
  });

  try {
    const response = await axios.patch<MCPConfiguration>(url, payload, {
      headers: {
        'Authorization': await AuthClientProvider.getInstance().getAuthToken(selectedEnv),
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "x-ms-correlation-id": correlationId
      },
    });

    Logger.Instance.trackTrace(`completed updateMCPConfig`, {
      url: url,
      correlationId: correlationId,
      response: response
    });

    return response.data;
  } catch (error) {
    Logger.Instance.trackException({ error: error as Error }, {
      event: `error updateMCPConfig`,
      url: url,
      correlationId: correlationId
    });
    throw error;
  }
};

/**
 * Save MCP Configuration as Draft and Increment Workflow Version
 * POST /{environment}/mcpconfigs/draft/{workflowId}
 */
export const saveMCPConfigDraft = async (
  workflowId: string,
  payload: MCPConfigInput,
  selectedEnv: string
): Promise<MCPConfiguration> => {
  const correlationId = uuidv4();
  const baseUrl = getMCPConfigBaseUrl(selectedEnv);
  
  if (!baseUrl) {
    throw new Error(`MCP Config API not available for environment: ${selectedEnv}`);
  }

  const url = `${baseUrl}/${selectedEnv}/mcpconfigs/draft/${workflowId}`;

  Logger.Instance.trackTrace(`start saveMCPConfigDraft`, {
    url: url,
    workflowId: workflowId,
    correlationId: correlationId,
    reqPayload: payload
  });

  try {
    const response = await axios.post<MCPConfiguration>(url, payload, {
      headers: {
        'Authorization': await AuthClientProvider.getInstance().getAuthToken(selectedEnv),
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "x-ms-correlation-id": correlationId,
        "CorrelationId": correlationId
      },
    });

    Logger.Instance.trackTrace(`completed saveMCPConfigDraft`, {
      url: url,
      workflowId: workflowId,
      correlationId: correlationId,
      response: response
    });

    return response.data;
  } catch (error) {
    Logger.Instance.trackException({ error: error as Error }, {
      event: `error saveMCPConfigDraft`,
      url: url,
      workflowId: workflowId,
      correlationId: correlationId
    });
    throw error;
  }
};

/**
 * Get published MCP Configuration by ID (for UAT→PROD propagation)
 * GET /{environment}/mcpconfigs/{id}/published
 */
export const getPublishedMCPConfigById = async (
  id: string,
  selectedEnv: string
): Promise<MCPConfiguration | null> => {
  const correlationId = uuidv4();
  const baseUrl = getMCPConfigBaseUrl(selectedEnv);
  const url = `${baseUrl}/${selectedEnv}/mcpconfigs/${id}/published`;
  
  if (!baseUrl) {
    throw new Error(`MCP Config API not available for environment: ${selectedEnv}`);
  }

  Logger.Instance.trackTrace(`start getPublishedMCPConfigById`, {
    url: url,
    correlationId: correlationId
  });

  try {
    const response = await axios.get<MCPConfiguration>(url, {
      headers: {
        'Authorization': await AuthClientProvider.getInstance().getAuthToken(selectedEnv),
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "x-ms-correlation-id": correlationId,
      },
    });

    Logger.Instance.trackTrace(`completed getPublishedMCPConfigById`, {
      url: url,
      correlationId: correlationId
    });

    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      // No published config found - this is okay, not an error
      Logger.Instance.trackTrace(`No published MCP config found`, {
        url: url,
        correlationId: correlationId
      });
      return null;
    }
    Logger.Instance.trackException({ error: error as Error }, {
      event: `error getPublishedMCPConfigById`,
      url: url,
      correlationId: correlationId
    });
    throw error;
  }
};

/**
 * Delete MCP Configuration
 * DELETE /{environment}/mcpconfigs/{id}
 */
export const deleteMCPConfig = async (
  id: string,
  selectedEnv: string
): Promise<void> => {
  const correlationId = uuidv4();
  const baseUrl = getMCPConfigFullUrl(selectedEnv);
  const url = `${baseUrl}/${id}`;
  
  if (!baseUrl) {
    throw new Error(`MCP Config API not available for environment: ${selectedEnv}`);
  }

  Logger.Instance.trackTrace(`start deleteMCPConfig`, {
    url: url,
    correlationId: correlationId
  });

  try {
    await axios.delete<void>(url, {
      headers: {
        'Authorization': await AuthClientProvider.getInstance().getAuthToken(selectedEnv),
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "x-ms-correlation-id": correlationId
      },
    });

    Logger.Instance.trackTrace(`completed deleteMCPConfig`, {
      url: url,
      correlationId: correlationId
    });
  } catch (error) {
    Logger.Instance.trackException({ error: error as Error }, {
      event: `error deleteMCPConfig`,
      url: url,
      correlationId: correlationId
    });
    throw error;
  }
};

/**
 * Propagate MCP Configuration (UAT → PROD)
 * POST /{environment}/mcpconfigs/propagate
 */
export const propagateMCPConfig = async (
  config: MCPConfiguration,
  selectedEnv: string
): Promise<MCPConfiguration> => {
  const correlationId = uuidv4();
  const baseUrl = getMCPConfigBaseUrl(selectedEnv);
  const url = `${baseUrl}/${selectedEnv}/mcpconfigs/propagate`;
  
  if (!baseUrl) {
    throw new Error(`MCP Config API not available for environment: ${selectedEnv}`);
  }

  Logger.Instance.trackTrace(`start propagateMCPConfig`, {
    url: url,
    insightType: config.InsightType,
    correlationId: correlationId
  });

  try {
    const response = await axios.post<MCPConfiguration>(url, config, {
      headers: {
        'Authorization': await AuthClientProvider.getInstance().getAuthToken(selectedEnv),
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "x-ms-correlation-id": correlationId,
        "CorrelationId": correlationId
      },
    });

    Logger.Instance.trackTrace(`completed propagateMCPConfig`, {
      url: url,
      insightType: config.InsightType,
      correlationId: correlationId
    });

    return response.data;
  } catch (error) {
    Logger.Instance.trackException({ error: error as Error }, {
      event: `error propagateMCPConfig`,
      url: url,
      insightType: config.InsightType,
      correlationId: correlationId
    });
    throw error;
  }
};
